/**
 * Partial fulfilment.
 *
 * A Sales Order line can be delivered in several tranches and invoiced in
 * several tranches. Available quantities are DERIVED from the link rows,
 * never from a counter the UI increments — a UI counter drifts the moment an
 * invoice is cancelled.
 *
 * Pure and side-effect free: the same function serves the editor (to cap an
 * input), the guard (to reject an over-issue), and the availability column
 * on a list. Three consumers, one rule, so they cannot disagree.
 */
import type { Quantity } from '@core/money';
import { asQuantity } from '@core/money';
import type { DocType } from './docTypes';

export type LinkRelation =
  | 'CONVERTED_FROM'
  | 'DELIVERED_AGAINST'
  | 'INVOICED_AGAINST'
  | 'PURCHASE_RECEIVED_AGAINST'
  | 'PURCHASE_BILLED_AGAINST'
  | 'RETURNED_AGAINST'
  | 'CREDITED_AGAINST'
  | 'SUPERSEDES';

export interface FulfilmentLink {
  relation: LinkRelation;
  /** Quantity this link consumes from the SOURCE line. */
  qty: Quantity;
  /**
   * The type of document that OWNS this link.
   *
   * Required because `INVOICED_AGAINST` alone is ambiguous: a Sales Invoice
   * raised from a Delivery Note and one raised straight from the Sales Order
   * share the same relation, but consume DIFFERENT capacity — the first
   * consumes delivered stock, the second consumes ordered stock. Without this
   * field a deliver-then-invoice sequence is counted as invoiced twice.
   */
  fromDocType: DocType;
}

/** Document types whose issue consumes DELIVERED quantity. */
const DELIVERY_TYPES: readonly DocType[] = ['DELIVERY_NOTE', 'PURCHASE_RECEIPT'];

export interface SourceLineSnapshot {
  lineId: string;
  /** Ordered / received quantity on the source line. */
  quantity: Quantity;
  /** Rollup maintained transactionally; treated as a hint, not truth. */
  fulfilledQty?: Quantity;
  invoicedQty?: Quantity;
  returnedQty?: Quantity;
}

export interface LineAvailability {
  lineId: string;
  orderedQty: Quantity;
  /** Delivered (or received) against this line so far. */
  deliveredQty: Quantity;
  /** Invoiced (or billed) from Delivery Notes. */
  invoicedFromDeliveriesQty: Quantity;
  /** Invoiced directly from the source, with no Delivery Note. */
  invoicedDirectQty: Quantity;
  returnedQty: Quantity;
  /** What a new Delivery Note may still take. */
  deliverableQty: Quantity;
  /** What an invoice may still take from delivered-but-uninvoiced stock. */
  invoiceableFromDeliveriesQty: Quantity;
  /** What an invoice may still take when deliver-and-invoice is allowed. */
  invoiceableDirectQty: Quantity;
  /** What a return may still take. */
  returnableQty: Quantity;
}

export interface FulfilmentSettings {
  /**
   * Deliver and invoice in one step. Many SMEs never raise a Delivery Note,
   * so the invoice may consume the source quantity directly. When false, an
   * invoice can only be raised from a Delivery Note.
   */
  allowInvoiceWithoutDelivery: boolean;
}

/**
 * Quantities are milli-units of possibly-fractional goods (0.75 Kg), and
 * 0.1 + 0.2 !== 0.3 in IEEE-754. Comparing with `===` produces phantom
 * "1 unit remaining" rows. This epsilon is the tolerance for that.
 */
const EPS = 1e-6;

const sum = (values: Quantity[]): Quantity =>
  asQuantity(Math.round(values.reduce((a, b) => a + b, 0)));

const clampNonNegative = (n: number): Quantity => asQuantity(Math.max(0, Math.round(n)));

/** Never let a float artefact hand out a fraction of a unit. */
function normalise(qty: Quantity): Quantity {
  const n = Number(qty);
  return Math.abs(n - Math.round(n)) < EPS ? asQuantity(Math.round(n)) : asQuantity(n);
}

export function getLineAvailability(
  source: SourceLineSnapshot,
  links: readonly FulfilmentLink[],
  settings: FulfilmentSettings,
): LineAvailability {
  const of = (relation: LinkRelation): Quantity =>
    sum(links.filter((l) => l.relation === relation).map((l) => l.qty));

  const deliveredQty = of('DELIVERED_AGAINST');
  const returnedQty = of('RETURNED_AGAINST');

  // Invoiced (or billed) split by whether the invoice came from a delivery.
  // Only the delivery-originated portion consumes delivered stock; a direct
  // invoice consumes ordered stock. Distinguishing them here is what stops a
  // deliver-then-invoice sequence being counted as invoiced twice.
  const isInvoicingLink = (l: FulfilmentLink): boolean =>
    l.relation === 'INVOICED_AGAINST' || l.relation === 'PURCHASE_BILLED_AGAINST';

  const invoicedViaDeliveryQty = sum(
    links.filter((l) => isInvoicingLink(l) && DELIVERY_TYPES.includes(l.fromDocType)).map((l) => l.qty),
  );
  const invoicedViaAnyLinkQty = sum(links.filter(isInvoicingLink).map((l) => l.qty));
  const invoicedFromDeliveriesQty = invoicedViaDeliveryQty;

  // Prefer the transactionally-maintained rollup. Fall back to the links when
  // it is absent, and count EVERY invoicing link — not just the
  // delivery-originated ones, or a direct invoice would read as zero invoiced.
  const invoicedTotal = source.invoicedQty
    ? normalise(source.invoicedQty)
    : invoicedViaAnyLinkQty;
  const invoicedDirectQty = clampNonNegative(invoicedTotal - invoicedViaDeliveryQty);

  // A return reduces what remains deliverable, but never below zero.
  const netDelivered = clampNonNegative(deliveredQty - returnedQty);
  const orderedQty = normalise(source.quantity);

  return {
    lineId: source.lineId,
    orderedQty,
    deliveredQty,
    invoicedFromDeliveriesQty,
    invoicedDirectQty,
    returnedQty,
    deliverableQty: clampNonNegative(orderedQty - netDelivered),
    invoiceableFromDeliveriesQty: clampNonNegative(netDelivered - invoicedFromDeliveriesQty),
    invoiceableDirectQty: settings.allowInvoiceWithoutDelivery
      ? clampNonNegative(orderedQty - invoicedTotal)
      : asQuantity(0),
    returnableQty: clampNonNegative(invoicedTotal - returnedQty),
  };
}

export type FulfilmentTarget = 'DELIVERY' | 'INVOICE' | 'RECEIPT' | 'BILL' | 'RETURN';

export type FulfilmentErrorCode =
  | 'QUANTITY_NOT_POSITIVE'
  | 'OVER_CONSUMPTION'
  | 'RETURN_EXCEEDS_INVOICED';

export interface FulfilmentError {
  code: FulfilmentErrorCode;
  message: string;
  requested: Quantity;
  available: Quantity;
}

export type FulfilmentCheck =
  | { ok: true }
  | { ok: false; error: FulfilmentError };

/**
 * The single rule that decides whether a consumption is legal.
 *
 * The editor calls it to cap an input; the service calls it to reject a
 * write. A rule that exists only in the UI is a rule that gets bypassed.
 */
export function checkConsumption(
  target: FulfilmentTarget,
  available: LineAvailability,
  requested: Quantity,
): FulfilmentCheck {
  const limit =
    target === 'DELIVERY' || target === 'RECEIPT'
      ? available.deliverableQty
      : target === 'INVOICE' || target === 'BILL'
        ? asQuantity(available.invoiceableFromDeliveriesQty + available.invoiceableDirectQty)
        : available.returnableQty;

  const fail = (code: FulfilmentErrorCode): FulfilmentCheck => ({
    ok: false,
    error: {
      code,
      message:
        code === 'QUANTITY_NOT_POSITIVE'
          ? 'Quantity must be greater than zero.'
          : `Requested ${requested / 1000} exceeds the ${limit / 1000} available.`,
      requested,
      available: limit,
    },
  });

  if (!(requested > 0)) return fail('QUANTITY_NOT_POSITIVE');
  if (requested - limit > EPS) {
    return fail(target === 'RETURN' ? 'RETURN_EXCEEDS_INVOICED' : 'OVER_CONSUMPTION');
  }
  return { ok: true };
}

/** Sum availability across a document, for the "available to promise" column. */
export function sumAvailability(
  lines: readonly LineAvailability[],
  pick: (a: LineAvailability) => Quantity,
): Quantity {
  return sum(lines.map(pick));
}
